<template>
  <v-dialog v-model="dialog" max-width="600" scrollable content-class="export-modal-dialog">
    <v-card class="export-modal" elevation="0">

      <!-- Header -->
      <div class="em-header">
        <div class="em-header-left">
          <div class="em-icon-wrap">
            <v-icon color="white" size="20">mdi-database-export-outline</v-icon>
          </div>
          <div>
            <div class="em-title">Mis datos personales</div>
            <div class="em-subtitle">Exportación según Ley 21.719</div>
          </div>
        </div>
        <v-btn icon small @click="close" class="em-close-btn">
          <v-icon color="rgba(255,255,255,0.7)" size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Body -->
      <div class="em-body">
        <div v-if="loading" class="em-loading">
          <v-progress-circular indeterminate color="#3EBFA3" size="40" width="3"></v-progress-circular>
          <p class="em-loading-text">Cargando tus datos...</p>
        </div>

        <div v-else-if="error" class="em-empty">
          <v-icon color="#F4516C" size="32">mdi-alert-circle-outline</v-icon>
          <p>No se pudieron cargar tus datos. Intenta nuevamente.</p>
        </div>

        <div v-else-if="exportData" class="em-content">

          <!-- Datos personales -->
          <div class="em-section">
            <div class="em-section-title">
              <v-icon size="14" color="#2D4B95" class="mr-1">mdi-account-outline</v-icon>
              Datos personales
            </div>
            <div class="em-rows">
              <div class="em-row">
                <span class="em-label">Nombre</span>
                <span class="em-value">{{ exportData.personal_data.name }}</span>
              </div>
              <div class="em-row">
                <span class="em-label">Email</span>
                <span class="em-value">{{ exportData.personal_data.email }}</span>
              </div>
              <div class="em-row">
                <span class="em-label">Miembro desde</span>
                <span class="em-value">{{ formatDate(exportData.personal_data.created_at) }}</span>
              </div>
              <template v-if="exportData.info">
                <div class="em-row">
                  <span class="em-label">Teléfono</span>
                  <span class="em-value">{{ exportData.info.phone || '—' }}</span>
                </div>
                <div class="em-row">
                  <span class="em-label">Dirección</span>
                  <span class="em-value">{{ exportData.info.address || '—' }}</span>
                </div>
              </template>
            </div>
          </div>

          <!-- Empresas -->
          <div class="em-section" v-if="exportData.companies && exportData.companies.length">
            <div class="em-section-title">
              <v-icon size="14" color="#2D4B95" class="mr-1">mdi-office-building-outline</v-icon>
              Empresas asociadas
            </div>
            <div class="em-chip-list">
              <span class="em-chip" v-for="(c, i) in exportData.companies" :key="i">{{ c.name }}</span>
            </div>
          </div>

          <!-- Proyectos -->
          <div class="em-section" v-if="exportData.projects && exportData.projects.length">
            <div class="em-section-title">
              <v-icon size="14" color="#2D4B95" class="mr-1">mdi-briefcase-outline</v-icon>
              Proyectos
            </div>
            <div class="em-rows">
              <div class="em-row" v-for="(p, i) in exportData.projects" :key="i">
                <span class="em-label">{{ p.name }}</span>
                <span class="em-value em-tag">{{ p.role || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- Consentimientos -->
          <div class="em-section" v-if="exportData.consent_logs && exportData.consent_logs.length">
            <div class="em-section-title">
              <v-icon size="14" color="#2D4B95" class="mr-1">mdi-shield-check-outline</v-icon>
              Historial de consentimientos
            </div>
            <div class="em-consent-list">
              <div class="em-consent-row" v-for="(cl, i) in exportData.consent_logs" :key="i">
                <v-icon size="14" :color="cl.accepted ? '#3EBFA3' : '#F4516C'" class="flex-shrink-0">
                  {{ cl.accepted ? 'mdi-check-circle' : 'mdi-close-circle' }}
                </v-icon>
                <div class="em-consent-info">
                  <span class="em-consent-type">{{ cl.consent_type }}</span>
                  <span class="em-consent-meta">{{ cl.source }} · {{ formatDate(cl.accepted_at || cl.created_at) }}</span>
                </div>
                <div class="em-consent-badge" :class="cl.accepted ? 'accepted' : 'revoked'">
                  {{ cl.accepted ? 'Aceptado' : 'Revocado' }}
                </div>
              </div>
            </div>
          </div>

          <p class="em-note">Exportado el {{ formatDate(exportData.exported_at) }}</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="em-footer">
        <div class="em-footer-left">
          <span class="em-footer-label">Descargar:</span>
          <button class="em-download-btn" :disabled="!exportData" @click="downloadAsJSON">
            <v-icon size="14" class="mr-1">mdi-code-json</v-icon>JSON
          </button>
          <button class="em-download-btn" :disabled="!exportData" @click="downloadAsCSV">
            <v-icon size="14" class="mr-1">mdi-file-delimited-outline</v-icon>CSV
          </button>
        </div>
        <button class="em-close-text-btn" @click="close">Cerrar</button>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import { exportUserData } from '@/helpers/api/user'

export default {
  name: 'UserDataExportModal',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
    error: false,
    exportData: null,
  }),
  computed: {
    dialog: {
      get() { return this.value },
      set(v) { this.$emit('input', v) },
    },
  },
  watch: {
    value(v) {
      if (v) this.loadData()
    },
  },
  methods: {
    close() {
      this.dialog = false
    },
    async loadData() {
      this.loading = true
      this.error = false
      this.exportData = null
      const res = await exportUserData()
      if (res && res.code === 200 && res.data) {
        this.exportData = res.data
      } else {
        this.error = true
      }
      this.loading = false
    },
    formatDate(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleDateString('es-CL', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    downloadAsJSON() {
      if (!this.exportData) return
      const blob = new Blob([JSON.stringify(this.exportData, null, 2)], { type: 'application/json' })
      this._triggerDownload(blob, 'mis-datos-laot.json')
    },
    downloadAsCSV() {
      if (!this.exportData) return
      const d = this.exportData
      const rows = [
        ['Campo', 'Valor'],
        ['Nombre', d.personal_data?.name || ''],
        ['Email', d.personal_data?.email || ''],
        ['Miembro desde', d.personal_data?.created_at || ''],
        ['Teléfono', d.info?.phone || ''],
        ['Dirección', d.info?.address || ''],
        [],
        ['Empresas'],
        ...(d.companies || []).map(c => ['', c.name || '']),
        [],
        ['Proyectos', 'Cargo'],
        ...(d.projects || []).map(p => [p.name || '', p.role || '']),
        [],
        ['Tipo de consentimiento', 'Versión', 'Aceptado', 'Fecha', 'Fuente'],
        ...(d.consent_logs || []).map(cl => [
          cl.consent_type || '',
          cl.consent_version || '',
          cl.accepted ? 'Sí' : 'No',
          cl.accepted_at || cl.created_at || '',
          cl.source || '',
        ]),
      ]
      const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n')
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      this._triggerDownload(blob, 'mis-datos-laot.csv')
    },
    _triggerDownload(blob, filename) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
* { box-sizing: border-box; }

.export-modal {
  border-radius: 16px !important;
  overflow: hidden;
  background: #ffffff;
  display: flex !important;
  flex-direction: column !important;
  max-height: 88vh;
}

/* Header */
.em-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 16px;
  background: linear-gradient(135deg, #2ea88f 0%, #1e7563 100%);
  flex-shrink: 0;
}
.em-header-left { display: flex; align-items: center; gap: 12px; }
.em-icon-wrap {
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(255,255,255,0.18);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.em-title { font-size: 14px; font-weight: 600; color: #fff; line-height: 1.2; }
.em-subtitle { font-size: 11px; color: rgba(255,255,255,0.65); margin-top: 2px; }
.em-close-btn { background: rgba(255,255,255,0.12) !important; border-radius: 8px !important; }

/* Body */
.em-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 22px;
}

.em-loading {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 48px 0; gap: 16px;
}
.em-loading-text { font-size: 13px; color: #8896b3; margin: 0; }

.em-empty {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 48px 0; gap: 12px;
  font-size: 13px; color: #8896b3; text-align: center;
}

/* Sections */
.em-section { margin-bottom: 20px; }
.em-section-title {
  font-size: 11px; font-weight: 600; color: #2D4B95;
  text-transform: uppercase; letter-spacing: 0.07em;
  display: flex; align-items: center; margin-bottom: 8px;
}

/* Rows */
.em-rows {
  border-radius: 10px; overflow: hidden;
  border: 1px solid #e2e8f4;
}
.em-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 9px 13px; gap: 12px;
  background: #f7f9fc;
  border-bottom: 1px solid #e8edf5;
}
.em-row:last-child { border-bottom: none; }
.em-label { font-size: 12px; font-weight: 600; color: #4a5568; min-width: 110px; flex-shrink: 0; }
.em-value { font-size: 12px; color: #2d3748; text-align: right; word-break: break-word; }
.em-tag {
  background: #eef2fb; color: #2D4B95;
  border-radius: 6px; padding: 2px 8px;
  font-size: 11px; font-weight: 600;
}

/* Chips */
.em-chip-list { display: flex; flex-wrap: wrap; gap: 6px; }
.em-chip {
  background: #eef2fb; color: #2D4B95;
  border-radius: 99px; padding: 4px 12px;
  font-size: 12px; font-weight: 500;
}

/* Consent list */
.em-consent-list {
  border-radius: 10px; overflow: hidden;
  border: 1px solid #e2e8f4;
}
.em-consent-row {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 13px; background: #f7f9fc;
  border-bottom: 1px solid #e8edf5;
}
.em-consent-row:last-child { border-bottom: none; }
.em-consent-info { flex: 1; min-width: 0; }
.em-consent-type { display: block; font-size: 12px; font-weight: 600; color: #2d3748; }
.em-consent-meta { display: block; font-size: 11px; color: #8896b3; margin-top: 1px; }
.em-consent-badge {
  flex-shrink: 0; font-size: 10px; font-weight: 700;
  border-radius: 6px; padding: 2px 7px;
}
.em-consent-badge.accepted { background: rgba(62,191,163,0.12); color: #2a9e84; }
.em-consent-badge.revoked { background: rgba(244,81,108,0.12); color: #d63050; }

.em-note {
  font-size: 10.5px; color: #aab4cc; text-align: right; margin: 8px 0 0;
}

/* Footer */
.em-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 22px; border-top: 1px solid #e8edf5; background: #f7f9fc;
  flex-shrink: 0; flex-wrap: wrap; gap: 8px;
}
.em-footer-left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.em-footer-label { font-size: 11.5px; color: #8896b3; font-weight: 500; }

.em-download-btn {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 6px 13px; border-radius: 8px;
  border: 1.5px solid #3EBFA3; background: transparent;
  font-size: 12px; font-weight: 600; color: #2a9e84; cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.em-download-btn:hover:not(:disabled) { background: #3EBFA3; color: #fff; }
.em-download-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.em-close-text-btn {
  padding: 6px 16px; border-radius: 8px;
  border: 1.5px solid #d1d9e8; background: transparent;
  font-size: 13px; font-weight: 500; color: #5a6a8a; cursor: pointer;
}
.em-close-text-btn:hover { background: #eef2fb; }
</style>

<style>
.export-modal-dialog.v-dialog { max-height: 92vh; }
</style>
